export const HTTP_METHODS = [
	"get",
	"post",
	"put",
	"patch",
	"delete",
	"head",
	"options",
	"trace",
] as const

export type HttpMethod = (typeof HTTP_METHODS)[number]

export type OperationKind = "endpoint" | "webhook"

/** One row of a parameter, body or response table. Children nest objects and array items. */
export interface SchemaNode {
	name?: string
	type: string
	required?: boolean
	nullable?: boolean
	description?: string
	format?: string
	enumValues?: string[]
	defaultValue?: string
	deprecated?: boolean
	readOnly?: boolean
	writeOnly?: boolean
	children?: SchemaNode[]
	variants?: { label: string; node: SchemaNode }[]
	/** A $ref cycle was cut here. */
	circular?: boolean
}

export interface ParamDoc {
	name: string
	in: "path" | "query" | "header" | "cookie"
	required: boolean
	deprecated?: boolean
	description?: string
	schema: SchemaNode
}

export interface BodyDoc {
	contentType: string
	required: boolean
	description?: string
	schema: SchemaNode
	example?: string
}

export interface ResponseDoc {
	status: string
	description?: string
	contentType?: string
	schema?: SchemaNode
	example?: string
}

export interface CodeSamples {
	curl: string
	javascript: string
	python: string
}

export interface OperationDoc {
	/** operationId, or "METHOD path" when the spec has none. Stable across syncs. */
	key: string
	kind: OperationKind
	groupSlug: string
	slug: string
	method: HttpMethod
	/** Endpoint path, or the event name for a webhook. */
	path: string
	title: string
	description?: string
	deprecated?: boolean
	/** Reachable by link but left out of the sidebar (x-hidden). */
	hidden?: boolean
	security: OperationAuthDoc[]
	params: ParamDoc[]
	body?: BodyDoc
	responses: ResponseDoc[]
	samples?: CodeSamples
}

export interface NavItem {
	key: string
	slug: string
	title: string
	method: HttpMethod
	path: string
	kind: OperationKind
	deprecated?: boolean
	hidden?: boolean
}

export interface NavGroup {
	slug: string
	/** null for the webhook group, which the reader UI labels itself. */
	name: string | null
	description?: string
	kind: OperationKind
	items: NavItem[]
}

export interface ApiAuthDoc {
	/** The scheme's key under components.securitySchemes. */
	name: string
	label: string
	description?: string
	/** The header, query parameter, or cookie the credential travels in. */
	field: string
	in: "header" | "query" | "cookie"
}

export interface OperationAuthDoc extends ApiAuthDoc {
	/** False when the endpoint accepts any one of several schemes. */
	required: boolean
}

// Bump when the bundle shape or its derived text changes; stored bundles rebuild from their spec.
export const API_REFERENCE_BUNDLE_VERSION = 5

/** Everything a reader page needs, built once per spec hash. */
export interface ApiReferenceBundle {
	version: typeof API_REFERENCE_BUNDLE_VERSION
	title: string
	apiVersion: string
	description?: string
	openapi: string
	servers: string[]
	auth: ApiAuthDoc[]
	groups: NavGroup[]
	/** Keyed by `${groupSlug}/${slug}`, the URL tail under the tab. */
	operations: Record<string, OperationDoc>
	warnings: string[]
}

export const WEBHOOK_GROUP_SLUG = "webhooks"

// Lives here rather than in parse.ts so the dashboard can check a file without loading the YAML parser.
export const MAX_SPEC_BYTES = 20 * 1024 * 1024
